/**
 * SearchListPopover — a single-pick list that opens under a trigger, with a
 * search box once the list is long (shouldOfferSearch; a shop group can have
 * 100+ locations). Arrow keys move, Enter picks, Escape closes and returns
 * focus to the trigger. The selected option is marked and scrolled into view.
 *
 * Portaled to document.body with fixed positioning so a modal's overflow
 * can't clip it (same approach as MultiSearchSelect).
 *
 * Design system: a menu on `surfaceRaised`, the search box the ds TextInput,
 * rows in `value`-size text (44px on a phone), group headings in `label`.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check } from 'lucide-react';
import { useResponsive } from '@turn-connect/hooks';
import { textRole, tokenVars } from '@turn-connect/ui';
import { TextInput } from './ds';
import { filterOptionsByWords, shouldOfferSearch, type SearchOption } from './filterOptionsByWords';

export interface SearchListPopoverProps {
  open: boolean;
  onClose: () => void;
  /** The element the popover opens under; focus returns here on close. */
  anchorRef: React.RefObject<HTMLElement | null>;
  options: SearchOption[];
  value: string;
  onSelect: (value: string) => void;
  /** Accessible name of the list, e.g. "Locations". */
  listLabel: string;
  searchPlaceholder?: string;
  /** Rows are `${testIdPrefix}-option-${value}`, the box `${testIdPrefix}-search`. */
  testIdPrefix: string;
  /** Which edge of the trigger the list lines up with (default right). */
  align?: 'left' | 'right';
}

const MAX_HEIGHT = 360;

const SearchListPopover: React.FC<SearchListPopoverProps> = ({
  open,
  onClose,
  anchorRef,
  options,
  value,
  onSelect,
  listLabel,
  searchPlaceholder = 'Search',
  testIdPrefix,
  align = 'right',
}) => {
  const listId = useId();
  const { isMobile } = useResponsive();
  const popoverRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const showSearch = shouldOfferSearch(options.length);
  const rows = useMemo(() => (showSearch ? filterOptionsByWords(options, query) : options), [showSearch, options, query]);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 16);
    // Right-aligned by default (a Change button sits at the right of its row);
    // a title opens it under its left edge.
    const start = align === 'left' ? rect.left : rect.right - width;
    const left = Math.max(8, Math.min(start, window.innerWidth - width - 8));
    const top = rect.bottom + 4;
    // Scrolling the list itself also lands here: skip when nothing moved.
    setPos((p) => (p && p.top === top && p.left === left && p.width === width ? p : { top, left, width }));
  }, [anchorRef, align]);

  // Fresh on every open: empty search, the current choice active.
  useLayoutEffect(() => {
    if (!open) return;
    setQuery('');
    const i = options.findIndex((o) => o.value === value);
    setActive(i >= 0 ? i : 0);
    place();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!popoverRef.current?.contains(t) && !anchorRef.current?.contains(t)) onClose();
    };
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, onClose, anchorRef, place]);

  // Keep the active row in view (also brings the current choice into view on open).
  useEffect(() => {
    if (!open || !rows[active]) return;
    document.getElementById(`${listId}-${rows[active].value}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open, rows, listId]);

  // Without a search box the list itself takes focus for the arrow keys.
  useEffect(() => {
    if (open && pos && !showSearch) listRef.current?.focus();
  }, [open, pos, showSearch]);

  const close = (refocus = true) => {
    onClose();
    if (refocus) anchorRef.current?.focus();
  };

  const pick = (v: string) => {
    onSelect(v);
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && rows[active]) {
      e.preventDefault();
      pick(rows[active].value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // don't close the surrounding modal too
      close();
    } else if (e.key === 'Tab') {
      // The list sits outside the modal's focus trap: close it and let Tab move on.
      close(false);
    }
  };

  if (!open || !pos || typeof document === 'undefined') return null;

  const activeId = rows[active] ? `${listId}-${rows[active].value}` : undefined;
  // Group headings only help when the list spans more than one group.
  const groups = [...new Set(options.map((o) => o.group).filter(Boolean))] as string[];
  const showGroups = groups.length > 1;
  // Each option is described by its group's heading, so a screen reader says
  // which account a location is in (two accounts can share a shop name).
  const groupId = (group: string) => `${listId}-group-${groups.indexOf(group)}`;

  return ReactDOM.createPortal(
    <div
      ref={popoverRef}
      data-testid={`${testIdPrefix}-popover`}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: `min(${MAX_HEIGHT}px, calc(100vh - ${pos.top + 8}px))`,
        zIndex: 100000,
        display: 'flex',
        flexDirection: 'column',
        background: tokenVars.surfaceRaised,
        border: `1px solid ${tokenVars.border}`,
        borderRadius: 8,
        boxShadow: `0 16px 40px ${tokenVars.overlay}`,
        padding: 8,
        boxSizing: 'border-box',
      }}
    >
      {showSearch && (
        <TextInput
          type="search"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-label={searchPlaceholder}
          aria-autocomplete="list"
          placeholder={searchPlaceholder}
          data-testid={`${testIdPrefix}-search`}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- opening a long list is asking to search
          autoFocus
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          style={{ marginBottom: 6, flex: 'none' }}
        />
      )}
      <ul
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={listLabel}
        aria-activedescendant={showSearch ? undefined : activeId}
        tabIndex={showSearch ? -1 : 0}
        onKeyDown={showSearch ? undefined : onKeyDown}
        style={{ listStyle: 'none', margin: 0, padding: 0, overflowY: 'auto', outline: 'none', minHeight: 0 }}
      >
        {rows.map((opt, i) => {
          const selected = opt.value === value;
          const heading = showGroups && opt.group && opt.group !== rows[i - 1]?.group ? opt.group : null;
          return (
            <React.Fragment key={opt.value}>
              {heading && (
                <li role="presentation" aria-hidden="true" id={groupId(heading)} style={{ ...textRole('label'), padding: '10px 10px 4px' }}>
                  {heading}
                </li>
              )}
              <li
                id={`${listId}-${opt.value}`}
                role="option"
                aria-selected={selected}
                aria-describedby={showGroups && opt.group ? groupId(opt.group) : undefined}
                data-testid={`${testIdPrefix}-option-${opt.value}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt.value)}
                style={{
                  ...textRole('body', tokenVars.text.primary),
                  fontWeight: selected ? 600 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  // A finger-sized row on a phone.
                  minHeight: isMobile ? 44 : 36,
                  boxSizing: 'border-box',
                  background: i === active ? tokenVars.washStrong : 'transparent',
                }}
              >
                <span style={{ minWidth: 0, overflowWrap: 'anywhere', flex: 1 }}>{opt.label}</span>
                {opt.detail && (
                  // A visual hint (e.g. active jobs) — kept out of the option's name.
                  <span aria-hidden="true" style={{ ...textRole('meta'), flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
                    {opt.detail}
                  </span>
                )}
                {selected ? (
                  <Check size={16} aria-hidden="true" style={{ color: tokenVars.textAccent, flex: 'none' }} />
                ) : (
                  opt.detail && <span aria-hidden="true" style={{ width: 16, flex: 'none' }} />
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
      {/* Always mounted so screen readers announce the message when it appears. */}
      <div aria-live="polite" style={{ ...textRole('meta'), padding: showSearch && rows.length === 0 ? '8px 4px' : 0 }}>
        {showSearch && rows.length === 0 ? `No matches for “${query.trim()}”` : null}
      </div>
    </div>,
    document.body
  );
};

export default SearchListPopover;
